import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Sparkles, Clock } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

export default function EventsListPage() {
  const { lang } = useI18n();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events/list").then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  const { upcoming, past } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const up = [], pa = [];
    for (const ev of events || []) {
      const d = ev.event_date || "";
      if (d >= today) up.push(ev); else pa.push(ev);
    }
    up.sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
    pa.sort((a, b) => (b.event_date || "").localeCompare(a.event_date || ""));
    return { upcoming: up, past: pa };
  }, [events]);

  return (
    <div className="min-h-screen bg-white text-charcoal" data-testid="events-list-page">
      <SiteHeader variant="light" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 sm:py-16 md:py-20">
        <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-4">
          {lang === "en" ? "All events" : "Tất cả sự kiện"}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-charcoal">
          {lang === "en" ? "Events" : "Sự Kiện"}
        </h1>
        <p className="mt-4 text-warmgray-700 max-w-2xl">
          {lang === "en"
            ? "Upcoming workshops and the archive of everything we've hosted so far."
            : "Sự kiện sắp diễn ra và lưu trữ các workshop đã tổ chức."}
        </p>

        {events.length === 0 && (
          <div className="mt-12 bg-warmgray-50 border border-dashed border-warmgray-300 rounded-3xl p-12 text-center text-warmgray-700">
            {lang === "en" ? "No events yet. Check back soon." : "Chưa có sự kiện. Vui lòng quay lại sau."}
          </div>
        )}

        {/* UPCOMING */}
        {upcoming.length > 0 && (
          <section className="mt-12 sm:mt-16" data-testid="events-upcoming-section">
            <div className="flex items-end justify-between mb-6 sm:mb-8">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-emerald2/10 text-emerald2 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em]">
                  <Sparkles className="w-3 h-3" />
                  {lang === "en" ? "Upcoming" : "Sắp diễn ra"}
                </div>
                <h2 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl text-charcoal">
                  {lang === "en" ? "Next up" : "Sắp tới"}
                </h2>
              </div>
              <span className="text-xs sm:text-sm text-warmgray-500 tabular-nums">
                {upcoming.length}{" "}
                {lang === "en"
                  ? (upcoming.length === 1 ? "event" : "events")
                  : "sự kiện"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {upcoming.map((ev) => (
                <UpcomingCard key={ev.slug} ev={ev} lang={lang} />
              ))}
            </div>
          </section>
        )}

        {/* PAST */}
        {past.length > 0 && (
          <section className="mt-16 sm:mt-24" data-testid="events-past-section">
            <div className="flex items-end justify-between mb-6 sm:mb-8">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-warmgray-200 text-warmgray-700 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em]">
                  <Clock className="w-3 h-3" />
                  {lang === "en" ? "Past" : "Đã diễn ra"}
                </div>
                <h2 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl text-warmgray-700">
                  {lang === "en" ? "Archive" : "Lưu trữ"}
                </h2>
              </div>
              <span className="text-xs sm:text-sm text-warmgray-500 tabular-nums">
                {past.length}{" "}
                {lang === "en"
                  ? (past.length === 1 ? "event" : "events")
                  : "sự kiện"}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {past.map((ev) => <PastCard key={ev.slug} ev={ev} lang={lang} />)}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function UpcomingCard({ ev, lang }) {
  const dp = parseDateParts(ev.event_date, lang);
  return (
    <Link
      to={`/nail-event/${ev.slug}`}
      data-testid={`event-card-${ev.slug}`}
      className="group relative block overflow-hidden rounded-3xl bg-white border border-warmgray-200 hover:-translate-y-1 hover:shadow-[0_25px_45px_-20px_rgba(15,26,46,0.35)] transition-all duration-300"
    >
      {/* Poster image */}
      <div className="relative aspect-[4/5] overflow-hidden">
        {ev.hero_image_url ? (
          <img
            src={ev.hero_image_url}
            alt={ev.name}
            className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-warmgray-800 to-charcoal" />
        )}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        {/* Date sticker */}
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-2xl px-3 py-2 shadow-lg text-center min-w-[56px]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald2 leading-none">{dp.monthShort}</div>
          <div className="text-2xl font-serif font-bold text-charcoal leading-none mt-0.5">{dp.day}</div>
          <div className="text-[9px] font-medium text-warmgray-500 leading-none mt-0.5">{dp.year}</div>
        </div>
        {/* Free chip */}
        <div className="absolute top-3 right-3 bg-emerald2 text-white rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest shadow">
          {lang === "en" ? "Free" : "Miễn phí"}
        </div>
        {/* Info overlay */}
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <h3 className="font-serif text-lg sm:text-xl leading-tight line-clamp-2">{ev.name}</h3>
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-white/85">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{ev.city || ev.location}{ev.state ? `, ${ev.state}` : ""}</span>
          </div>
        </div>
      </div>
      {/* CTA row */}
      <div className="flex items-center justify-between px-4 py-3 bg-white">
        <span className="text-[11px] uppercase tracking-widest font-semibold text-warmgray-500 inline-flex items-center gap-1.5">
          <Calendar className="w-3 h-3" />
          {ev.event_time || dp.dayOfWeek}
        </span>
        <span className="inline-flex items-center gap-1 text-emerald2 font-medium text-xs group-hover:gap-2 transition-all">
          {lang === "en" ? "Reserve" : "Đặt chỗ"} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function PastCard({ ev, lang }) {
  const dp = parseDateParts(ev.event_date, lang);
  return (
    <Link
      to={`/nail-event/${ev.slug}`}
      data-testid={`past-event-${ev.slug}`}
      className="group relative block aspect-[4/5] overflow-hidden rounded-2xl bg-charcoal opacity-90 grayscale-[0.3] hover:grayscale-0 hover:opacity-100 transition-all duration-500"
    >
      {ev.hero_image_url ? (
        <img src={ev.hero_image_url} alt={ev.name} className="absolute inset-0 w-full h-full object-cover object-top" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-warmgray-700 to-charcoal" />
      )}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/95 via-black/60 to-transparent" />
      <div className="absolute top-2 left-2 bg-warmgray-800/85 backdrop-blur text-white/95 rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">
        {lang === "en" ? "Ended" : "Đã kết thúc"}
      </div>
      <div className="absolute top-2 right-2 bg-white/90 rounded-lg px-2 py-1 text-center min-w-[40px]">
        <div className="text-[8px] font-bold uppercase tracking-widest text-warmgray-600 leading-none">{dp.monthShort}</div>
        <div className="text-sm font-serif font-bold text-charcoal leading-none mt-0.5">{dp.day}</div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <h3 className="font-serif text-sm sm:text-base leading-tight line-clamp-2">{ev.name}</h3>
        <p className="mt-1 text-[10px] text-white/70 line-clamp-1">{ev.city || ev.location}{ev.state ? `, ${ev.state}` : ""}</p>
      </div>
    </Link>
  );
}

function parseDateParts(raw, lang) {
  if (!raw) return { day: "--", monthShort: "", year: "", dayOfWeek: "" };
  const d = new Date(raw);
  if (isNaN(d.getTime())) return { day: "--", monthShort: "", year: "", dayOfWeek: "" };
  const locale = lang === "en" ? "en-US" : "vi-VN";
  return {
    day: d.getUTCDate().toString().padStart(2, "0"),
    monthShort: d.toLocaleDateString(locale, { month: "short", timeZone: "UTC" }).replace(".", ""),
    year: d.getUTCFullYear().toString(),
    dayOfWeek: d.toLocaleDateString(locale, { weekday: "long", timeZone: "UTC" }),
  };
}
